/**
 * @file The projection that owns one row, or `null`. Consumers: `ArRow` (which asks
 * `deriveCellTooltipOfColumn` for each cell it renders) and `useRowDeleteTargets`
 * (the blocked Delete). Owning a row is not owning every cell of it: the
 * projection's `columns` list decides that, and the columns beside them stay
 * editable.
 * Invariant: authority is the row ID namespace via `formDeriveIdOfRowId`, NEVER
 * the row's `deriveOwner` marker — that field is client-writable, so a forged one
 * must grant nothing and a stripped one must take nothing away. A `drv~` row whose
 * deriveId no longer appears in this table's config answers `null`: nothing can be
 * said about it, so nothing is claimed.
 */

import { formDeriveIdOfRowId } from '#types';
import type { ArRowId } from '#types';
import type { ArTableDeriveProjection } from './resolveArTableDeriveProjections';

function deriveProjectionOfRow(
  projections: readonly ArTableDeriveProjection[],
  rowId: ArRowId,
): ArTableDeriveProjection | null {
  if (projections.length === 0) return null;
  const deriveId = formDeriveIdOfRowId(rowId);
  if (deriveId === null) return null;
  return projections.find((projection) => projection.deriveId === deriveId) ?? null;
}

export { deriveProjectionOfRow };
